import { useState, useEffect, useMemo } from 'react'
import {
  Loader2, Plus, Trash2, Copy, Check,
  ChevronDown, ChevronRight as ChevronRightIcon, Save, RotateCcw, Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { useAssistant, useUpdateAssistant } from '@/hooks/useAssistants'

// ── Types & Defaults ──────────────────────────────────────────────────────

interface FactItem {
  category: string
  key: string
  description: string
}

const DEFAULT_FACTS: FactItem[] = [
  { category: 'identity', key: 'name', description: "Customer's full name" },
  { category: 'identity', key: 'email', description: 'Email address' },
  { category: 'identity', key: 'phone', description: 'Phone number' },
  { category: 'preference', key: 'language', description: 'Preferred language' },
]

const PRESET_TEMPLATES: { label: string; facts: FactItem[] }[] = [
  {
    label: 'Customer Support',
    facts: [
      { category: 'identity', key: 'name', description: "Customer's full name" },
      { category: 'identity', key: 'email', description: 'Email address' },
      { category: 'identity', key: 'phone', description: 'Phone number' },
      { category: 'account', key: 'account_number', description: 'Account or ticket number' },
      { category: 'preference', key: 'language', description: 'Preferred language' },
    ],
  },
  {
    label: 'Municipal Services (311)',
    facts: [
      { category: 'identity', key: 'name', description: 'Resident name' },
      { category: 'identity', key: 'email', description: 'Email address' },
      { category: 'identity', key: 'phone', description: 'Phone number' },
      { category: 'location', key: 'address', description: 'Street address' },
      { category: 'location', key: 'unit_number', description: 'Apartment or unit number' },
      { category: 'account', key: 'account_number', description: 'Utility account number' },
    ],
  },
  {
    label: 'Sales / CRM',
    facts: [
      { category: 'identity', key: 'name', description: 'Prospect name' },
      { category: 'identity', key: 'email', description: 'Work email' },
      { category: 'identity', key: 'phone', description: 'Phone number' },
      { category: 'company', key: 'company_name', description: 'Company name' },
      { category: 'company', key: 'role', description: 'Job title or role' },
      { category: 'interest', key: 'product', description: 'Product or plan of interest' },
      { category: 'preference', key: 'budget', description: 'Budget range' },
    ],
  },
]

// ── Auto-generate prompt from fact schema ─────────────────────────────────

function buildPromptFromSchema(facts: FactItem[]): string {
  if (facts.length === 0) return ''

  const grouped: Record<string, FactItem[]> = {}
  for (const f of facts) {
    if (f.category && f.key) {
      ;(grouped[f.category] ??= []).push(f)
    }
  }

  const lines = Object.entries(grouped).map(([cat, items]) => {
    const keys = items.map((it) => it.key).join(', ')
    return `- category: ${cat} \u2014 keys: ${keys}`
  })

  return `You are a fact extractor. Given a user message from a chat conversation, extract any of the following facts if explicitly stated by the user. Return ONLY a JSON array of objects with keys "category", "key", "value". If nothing is extractable, return an empty array [].

Valid categories and keys:
${lines.join('\n')}

Do not infer or guess. Only extract facts the user explicitly stated.`
}

// ── Prompt Preview ────────────────────────────────────────────────────────

function PromptPreview({ prompt }: { prompt: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(prompt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (!prompt) return null

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Prompt Preview</Label>
        <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 text-xs gap-1.5">
          {copied ? (
            <><Check className="h-3 w-3 text-green-600" />Copied</>
          ) : (
            <><Copy className="h-3 w-3" />Copy</>
          )}
        </Button>
      </div>
      <div className="rounded-lg border bg-muted/40 p-4 max-h-[240px] overflow-auto">
        <pre className="text-xs whitespace-pre-wrap font-mono text-muted-foreground leading-relaxed">
          {prompt}
        </pre>
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────

interface MemoryTabProps {
  assistantId: string
}

export function MemoryTab({ assistantId }: MemoryTabProps) {
  const { toast } = useToast()
  const { data: assistant, isLoading } = useAssistant(assistantId)
  const updateAssistant = useUpdateAssistant()

  const [enabled, setEnabled] = useState(false)
  const [facts, setFacts] = useState<FactItem[]>([])
  const [customPrompt, setCustomPrompt] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [dirty, setDirty] = useState(false)

  // Hydrate from API
  useEffect(() => {
    if (assistant) {
      setEnabled(assistant.memory_enabled ?? false)
      setFacts(
        (assistant.memory_fact_schema as FactItem[])?.length > 0
          ? (assistant.memory_fact_schema as FactItem[])
          : []
      )
      setCustomPrompt(assistant.memory_extraction_prompt ?? '')
      setDirty(false)
    }
  }, [assistant])

  // Live prompt preview — custom prompt takes precedence, otherwise auto-generated
  const activePrompt = useMemo(() => {
    if (customPrompt.trim()) return customPrompt.trim()
    return buildPromptFromSchema(facts)
  }, [facts, customPrompt])

  const handleSave = async () => {
    try {
      await updateAssistant.mutateAsync({
        assistantId,
        data: {
          memory_enabled: enabled,
          memory_fact_schema: facts,
          memory_extraction_prompt: customPrompt,
        },
      })
      setDirty(false)
      toast({ title: 'Memory settings saved' })
    } catch {
      toast({ title: 'Failed to save', variant: 'destructive' })
    }
  }

  const handlePreset = (preset: typeof PRESET_TEMPLATES[0]) => {
    setFacts(preset.facts)
    setCustomPrompt('')
    setDirty(true)
  }

  const handleResetDefaults = () => {
    setFacts(DEFAULT_FACTS)
    setCustomPrompt('')
    setDirty(true)
  }

  const addFact = () => {
    setFacts([...facts, { category: '', key: '', description: '' }])
    setDirty(true)
  }

  const removeFact = (index: number) => {
    setFacts(facts.filter((_, i) => i !== index))
    setDirty(true)
  }

  const updateFact = (index: number, field: keyof FactItem, value: string) => {
    setFacts(facts.map((f, i) => (i === index ? { ...f, [field]: value } : f)))
    setDirty(true)
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-primary" />
              <div>
                <CardTitle className="text-base">What should this assistant remember?</CardTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Define what information the assistant should pick up from conversations and remember across sessions.
                </p>
              </div>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={(v) => { setEnabled(v); setDirty(true) }}
            />
          </div>
        </CardHeader>

        {enabled && (
          <CardContent className="space-y-6 pt-0">
            {/* Quick Start Presets */}
            {facts.length === 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Quick start — choose a template</Label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_TEMPLATES.map((preset) => (
                    <Button
                      key={preset.label}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreset(preset)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Fact Schema Table */}
            {facts.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Things to remember</Label>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={handleResetDefaults} className="text-xs">
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Reset
                    </Button>
                    <Select onValueChange={(label) => {
                      const preset = PRESET_TEMPLATES.find(p => p.label === label)
                      if (preset) handlePreset(preset)
                    }}>
                      <SelectTrigger className="h-8 w-[160px] text-xs">
                        <SelectValue placeholder="Load template..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PRESET_TEMPLATES.map((p) => (
                          <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-[160px]">Group</TableHead>
                        <TableHead className="w-[160px]">Fact Name</TableHead>
                        <TableHead>Description (helps the AI understand what to look for)</TableHead>
                        <TableHead className="w-[50px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {facts.map((fact, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Input
                              value={fact.category}
                              onChange={(e) => updateFact(idx, 'category', e.target.value)}
                              placeholder="e.g. identity"
                              className="h-8 text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={fact.key}
                              onChange={(e) => updateFact(idx, 'key', e.target.value)}
                              placeholder="e.g. name"
                              className="h-8 text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={fact.description}
                              onChange={(e) => updateFact(idx, 'description', e.target.value)}
                              placeholder="e.g. Customer's full name"
                              className="h-8 text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => removeFact(idx)} className="h-8 w-8 p-0">
                              <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <Button variant="outline" size="sm" onClick={addFact}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add another fact
                </Button>
              </div>
            )}

            {/* Prompt Preview — always visible when there are facts */}
            {activePrompt && <PromptPreview prompt={activePrompt} />}

            {/* Advanced: Custom Prompt Override */}
            <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
              <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                {showAdvanced ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
                Advanced: Override extraction prompt
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Write your own prompt instead of the auto-generated one above.
                    When set, this overrides the auto-generated prompt entirely.
                    Clear this field to go back to auto-generation from the facts table.
                  </p>
                  <Textarea
                    value={customPrompt}
                    onChange={(e) => { setCustomPrompt(e.target.value); setDirty(true) }}
                    placeholder="Leave empty to auto-generate from the facts above..."
                    rows={8}
                    className="text-xs font-mono"
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Save */}
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={!dirty || updateAssistant.isPending}>
                {updateAssistant.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Memory Settings
              </Button>
            </div>
          </CardContent>
        )}

        {!enabled && (
          <CardContent className="pt-0">
            <p className="text-sm text-muted-foreground">
              When enabled, the assistant will automatically pick up and remember details
              like names, emails, and preferences from conversations. These are injected
              back into future conversations so the assistant can provide personalized responses.
            </p>
          </CardContent>
        )}
      </Card>
    </div>
  )
}
